import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/demo-container.default.js';
import * as wireframeSkin from './skins/demo-container.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

const _VALID_BACKGROUNDS = ['default', 'light', 'dark', 'checkerboard'];
const _VALID_PADDINGS = ['none', 'sm', 'md', 'lg'];

export const DEMO_CONTAINER_BACKGROUNDS = Object.freeze([..._VALID_BACKGROUNDS]);
export const DEMO_CONTAINER_PREVIEW_PADDINGS = Object.freeze([..._VALID_PADDINGS]);

let _captionIdSeq = 0;

class RunDemoContainer extends RunElement {
  #code = '';
  #language = 'html';
  #frameworks = '';
  #caption = '';
  #background = 'default';
  #previewPadding = 'md';
  #rendering = false;
  #captionId = `dc-caption-${++_captionIdSeq}`;
  #captionSlotHasContent = false;
  #lightDomObserver = null;

  static get observedAttributes() {
    return ['code', 'language', 'frameworks', 'caption', 'background', 'preview-padding'];
  }

  _onSlotChangeBound = (e) => this.#handleSlotChange(e);

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'figure');
    // Eagerly detect slotted caption children present at connect time
    this.#captionSlotHasContent = this.querySelectorAll('[slot="caption"]').length > 0;
    // Watch light DOM for dynamic slot="caption" additions/removals
    this.#lightDomObserver = new MutationObserver(() => {
      const has = this.querySelectorAll('[slot="caption"]').length > 0;
      if (has !== this.#captionSlotHasContent) {
        this.#captionSlotHasContent = has;
        this.requestUpdate();
      }
    });
    this.#lightDomObserver.observe(this, { childList: true });
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#lightDomObserver?.disconnect();
    this.#lightDomObserver = null;
    this.shadowRoot?.querySelectorAll('slot[name]').forEach(slot => {
      slot.removeEventListener('slotchange', this._onSlotChangeBound);
    });
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'code':            this.code = newValue ?? ''; break;
      case 'language':        this.language = newValue ?? 'html'; break;
      case 'frameworks':      this.frameworks = newValue ?? ''; break;
      case 'caption':         this.caption = newValue ?? ''; break;
      case 'background':      this.background = newValue ?? 'default'; break;
      case 'preview-padding': this.previewPadding = newValue ?? 'md'; break;
    }
  }

  get code() { return this.#code; }
  set code(value) {
    const isObj = value !== null && value !== undefined && typeof value === 'object';
    const coerced = isObj ? value : String(value ?? '');
    if (this.#code === coerced) return;
    this.#code = coerced;
    this.requestUpdate();
  }

  get language() { return this.#language; }
  set language(value) {
    const coerced = String(value ?? '').trim() || 'html';
    if (this.#language === coerced) return;
    this.#language = coerced;
    this.setAttribute('language', coerced);
    this.requestUpdate();
  }

  get frameworks() { return this.#frameworks; }
  set frameworks(value) {
    const coerced = String(value ?? '').trim();
    if (this.#frameworks === coerced) return;
    this.#frameworks = coerced;
    this.setAttribute('frameworks', coerced);
    this.requestUpdate();
  }

  get caption() { return this.#caption; }
  set caption(value) {
    const coerced = String(value ?? '').trim();
    if (this.#caption === coerced) return;
    this.#caption = coerced;
    this.setAttribute('caption', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  get background() { return this.#background; }
  set background(value) {
    const str = String(value ?? '');
    const coerced = _VALID_BACKGROUNDS.includes(str) ? str : 'default';
    if (this.#background === coerced) {
      if (this.getAttribute('background') !== coerced) this.setAttribute('background', coerced);
      return;
    }
    this.#background = coerced;
    this.setAttribute('background', coerced);
    this.requestUpdate();
  }

  get previewPadding() { return this.#previewPadding; }
  set previewPadding(value) {
    const str = String(value ?? '');
    const coerced = _VALID_PADDINGS.includes(str) ? str : 'md';
    if (this.#previewPadding === coerced) {
      if (this.getAttribute('preview-padding') !== coerced) this.setAttribute('preview-padding', coerced);
      return;
    }
    this.#previewPadding = coerced;
    this.setAttribute('preview-padding', coerced);
    this.requestUpdate();
  }

  get _captionId() { return this.#captionId; }
  get _hasCaptionSlot() { return this.#captionSlotHasContent; }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
    this.#attachSlotListeners();
    this.#propagateToChildren();
    this.#updateAria();
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #attachSlotListeners() {
    this.shadowRoot.querySelectorAll('slot[name]').forEach(slot => {
      slot.removeEventListener('slotchange', this._onSlotChangeBound);
      slot.addEventListener('slotchange', this._onSlotChangeBound);
    });
  }

  #handleSlotChange(event) {
    const slot = event.target;
    const assigned = slot.assignedElements({ flatten: true });
    const hasContent = assigned.length > 0;
    this.toggleAttribute(`data-has-${slot.name}`, hasContent);
    if (slot.name === 'caption') {
      this.#captionSlotHasContent = hasContent;
      this.#updateAria();
    }
  }

  #propagateToChildren() {
    const codeDisplay = this.shadowRoot.querySelector('run-code-display');
    if (!codeDisplay) return;
    codeDisplay.code = this.#code;
    codeDisplay.setAttribute('language', this.#language);
    if (this.#frameworks) {
      codeDisplay.setAttribute('frameworks', this.#frameworks);
    } else {
      codeDisplay.removeAttribute('frameworks');
    }
  }

  #updateAria() {
    if (!this.shadowRoot) return;
    this.setAttribute('role', 'figure');
    const captionIsPresent = this.#caption.length > 0 || this.#captionSlotHasContent;
    const previewEl = this.shadowRoot.querySelector('[part="preview"]');
    const captionEl = this.shadowRoot.querySelector('[part="caption"]');
    if (captionIsPresent) {
      if (captionEl) captionEl.setAttribute('id', this.#captionId);
      if (previewEl) previewEl.setAttribute('aria-describedby', this.#captionId);
    } else {
      if (previewEl) previewEl.removeAttribute('aria-describedby');
    }
  }
}

if (!customElements.get('run-demo-container')) {
  customElements.define('run-demo-container', RunDemoContainer);
}

export { RunDemoContainer };
export default RunDemoContainer;